import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getStrategies, createStrategy, updateStrategy, deleteStrategy, seedStrategies,
  getRules, addRule, updateRule, deleteRule, reorderRules,
  runScreener, getAiAnalysis,
  getSignalsForSymbol,
  getValidationEntries, addValidationEntry, deleteValidationEntry, updateValidationOutcome,
  linkPaperTrade,
} from "./StrategyService";
import { getPortfolios, executeTrade } from "./PaperTradingService";

const RULE_TYPES = [
  { value: "RSI_BELOW",          label: "RSI below threshold",     hasThreshold: true  },
  { value: "RSI_ABOVE",          label: "RSI above threshold",     hasThreshold: true  },
  { value: "PRICE_ABOVE_SMA",    label: "Price above SMA",         hasPeriod:    true  },
  { value: "PRICE_BELOW_SMA",    label: "Price below SMA",         hasPeriod:    true  },
  { value: "SMA_GOLDEN_CROSS",   label: "Golden cross (SMA20/50)"                      },
  { value: "SMA_DEATH_CROSS",    label: "Death cross (SMA20/50)"                       },
  { value: "MACD_BULLISH_CROSS", label: "MACD bullish cross"                           },
  { value: "MACD_BEARISH_CROSS", label: "MACD bearish cross"                           },
  { value: "PROFIT_TARGET",      label: "Profit target hit",       hasPct:       true  },
  { value: "STOP_LOSS",          label: "Stop-loss hit",           hasPct:       true  },
  { value: "CUSTOM",             label: "Custom / learned rule",   isCustom:     true  },
];

const SIGNAL_COLOR = { BUY: "#22c55e", SELL: "#ef4444" };
const AI_COLOR     = { BUY: "#22c55e", SELL: "#ef4444", HOLD: "#f59e0b" };
const CONF_COLOR   = { HIGH: "#22c55e", MEDIUM: "#f59e0b", LOW: "#94a3b8" };

function buildParams(ruleType, thresh, period, pct) {
  const rt = RULE_TYPES.find(r => r.value === ruleType);
  if (rt?.hasThreshold) return JSON.stringify({ threshold: parseFloat(thresh) || 30 });
  if (rt?.hasPeriod)    return JSON.stringify({ period: parseInt(period)     || 20 });
  if (rt?.hasPct)       return JSON.stringify({ pct: parseFloat(pct)         || 5  });
  return "{}";
}

function renderRuleParams(params) {
  if (!params || params === "{}") return null;
  try {
    const p = JSON.parse(params);
    if (!p || typeof p !== "object" || Array.isArray(p)) return null;
    const entries = Object.entries(p);
    if (entries.length === 0) return null;
    return (
      <div className="strat-rule-params">
        {entries.map(([k, v]) => <span key={k} className="strat-param-chip">{k}: {v}</span>)}
      </div>
    );
  } catch { return null; }
}

// Group flat rule list into ordered array of { groupName, rules[] }
function groupRules(rules) {
  const map = new Map();
  rules.forEach(r => {
    const g = r.groupName || "General";
    if (!map.has(g)) map.set(g, { groupName: g, minPos: r.position ?? 999, rules: [] });
    const grp = map.get(g);
    grp.rules.push(r);
    if ((r.position ?? 999) < grp.minPos) grp.minPos = r.position ?? 999;
  });
  const groups = [...map.values()];
  groups.sort((a, b) => a.minPos - b.minPos);
  groups.forEach(g => g.rules.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)));
  return groups;
}

// Flatten ordered groups back to a position-updated rule list
function flattenGroups(groups) {
  const flat = [];
  let pos = 0;
  groups.forEach(g => {
    g.rules.forEach(r => {
      flat.push({ ...r, position: pos++, groupName: g.groupName });
    });
  });
  return flat;
}

export default function TradingStrategyPage({ onOpenChart }) {
  const [strategies,      setStrategies]      = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [selected,        setSelected]        = useState(null);
  const [rules,           setRules]           = useState([]);
  const [rulesLoading,    setRulesLoading]    = useState(false);
  const [detailTab,       setDetailTab]       = useState("playbook"); // playbook | ai | screener
  const [screenerData,    setScreenerData]    = useState([]);
  const [screenerLoading, setScreenerLoading] = useState(false);

  // New strategy form
  const [showNewStrat, setShowNewStrat] = useState(false);
  const [stratName,    setStratName]    = useState("");
  const [stratDesc,    setStratDesc]    = useState("");
  const [stratTags,    setStratTags]    = useState("");

  // Rule form
  const [showRuleForm,  setShowRuleForm]  = useState(false);
  const [editingRule,   setEditingRule]   = useState(null);
  const [ruleSignal,    setRuleSignal]    = useState("BUY");
  const [ruleType,      setRuleType]      = useState("CUSTOM");
  const [ruleThresh,    setRuleThresh]    = useState("30");
  const [rulePeriod,    setRulePeriod]    = useState("20");
  const [rulePct,       setRulePct]       = useState("5");
  const [ruleDesc,      setRuleDesc]      = useState("");
  const [ruleLearnedOn, setRuleLearnedOn] = useState("");
  const [ruleGroupName, setRuleGroupName] = useState("General");

  // AI analysis
  const [aiSymbol,  setAiSymbol]  = useState("");
  const [aiResult,  setAiResult]  = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError,   setAiError]   = useState("");

  // Validate tab
  const [valSymbol,       setValSymbol]       = useState("");
  const [valSignals,      setValSignals]       = useState(null);
  const [valLoading,      setValLoading]       = useState(false);
  const [valEntries,      setValEntries]       = useState([]);
  const [valEntriesLoad,  setValEntriesLoad]   = useState(false);
  const [activeRule,      setActiveRule]       = useState(null);
  const [noteText,        setNoteText]         = useState("");
  const [noteSaving,      setNoteSaving]       = useState(false);
  const [updatingOutcome, setUpdatingOutcome]   = useState(null); // entry id being updated

  // Paper trade modal
  const [ptModal,       setPtModal]       = useState(null); // { symbol, ruleRef, sourceEntryId } | null
  const [ptPortfolios,  setPtPortfolios]  = useState([]);
  const [ptPortfolioId, setPtPortfolioId] = useState("");
  const [ptTradeType,   setPtTradeType]   = useState("BUY");
  const [ptQty,         setPtQty]         = useState("1");
  const [ptPrice,       setPtPrice]       = useState("");
  const [ptNotes,       setPtNotes]       = useState("");
  const [ptSubmitting,  setPtSubmitting]  = useState(false);
  const [ptError,       setPtError]       = useState("");

  // DnD for rule pills
  const dragPill = useRef(null);

  // DnD state
  const dragGroup = useRef(null);
  const dragRule  = useRef(null);

  const loadStrategies = useCallback(async () => {
    setLoading(true);
    const list = await getStrategies();
    setStrategies(list);
    if (!selected && list.length > 0) setSelected(list[0].id);
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadStrategies(); }, [loadStrategies]);

  const loadRules = useCallback(async (sid) => {
    if (!sid) return;
    setRulesLoading(true);
    const list = await getRules(sid);
    setRules(list);
    setRulesLoading(false);
  }, []);

  useEffect(() => { loadRules(selected); }, [selected, loadRules]);

  // ── Screener ──────────────────────────────────────────────────────────────
  const loadScreener = useCallback(async () => {
    setScreenerLoading(true);
    const data = await runScreener();
    setScreenerData(data);
    setScreenerLoading(false);
  }, []);

  useEffect(() => {
    if (detailTab === "screener") loadScreener();
  }, [detailTab, loadScreener]);

  useEffect(() => {
    if (detailTab === "validate" && selected) {
      loadValEntries(undefined);
    }
  }, [detailTab, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Strategy CRUD ─────────────────────────────────────────────────────────
  const handleSeed = async () => {
    const list = await seedStrategies();
    setStrategies(list);
    if (list.length) setSelected(list[0].id);
  };

  const handleCreateStrategy = async () => {
    if (!stratName.trim()) return;
    const s = await createStrategy({ name: stratName, description: stratDesc, tags: stratTags });
    if (s) {
      setStrategies(prev => [s, ...prev]);
      setSelected(s.id);
    }
    setShowNewStrat(false); setStratName(""); setStratDesc(""); setStratTags("");
  };

  const handleToggleActive = async (s) => {
    const updated = await updateStrategy(s.id, { active: !s.active });
    if (updated) setStrategies(prev => prev.map(x => x.id === s.id ? updated : x));
  };

  const handleDeleteStrategy = async (id) => {
    if (!window.confirm("Delete this strategy and all its rules?")) return;
    await deleteStrategy(id);
    setStrategies(prev => prev.filter(s => s.id !== id));
    if (selected === id) {
      const remaining = strategies.filter(s => s.id !== id);
      setSelected(remaining[0]?.id || null);
    }
  };

  // ── Rule CRUD ──────────────────────────────────────────────────────────────
  const openRuleForm = (rule, defaultGroup) => {
    if (rule) {
      setEditingRule(rule);
      setRuleSignal(rule.signal || "BUY");
      setRuleType(rule.ruleType || "CUSTOM");
      setRuleDesc(rule.description || "");
      setRuleLearnedOn(rule.learnedOn || "");
      setRuleGroupName(rule.groupName || "General");
      try {
        const p = JSON.parse(rule.parameters || "{}");
        if (p.threshold !== undefined) setRuleThresh(String(p.threshold));
        if (p.period    !== undefined) setRulePeriod(String(p.period));
        if (p.pct       !== undefined) setRulePct(String(p.pct));
      } catch {} // eslint-disable-line no-empty
    } else {
      setEditingRule(null);
      setRuleSignal("BUY"); setRuleType("CUSTOM"); setRuleThresh("30");
      setRulePeriod("20"); setRulePct("5"); setRuleDesc(""); setRuleLearnedOn("");
      setRuleGroupName(defaultGroup || "General");
    }
    setShowRuleForm(true);
  };

  const handleSaveRule = async () => {
    if (ruleType === "CUSTOM" && !ruleDesc.trim()) {
      alert("A description is required for Custom / learned rules — it's how the rule is identified in the Validate tab.");
      return;
    }
    const newPos  = rules.filter(r => (r.groupName || "General") === ruleGroupName).length;
    const data = {
      signal:      ruleSignal,
      ruleType,
      parameters:  buildParams(ruleType, ruleThresh, rulePeriod, rulePct),
      description: ruleDesc || null,
      learnedOn:   ruleLearnedOn || null,
      groupName:   ruleGroupName,
      position:    editingRule ? (editingRule.position ?? 0) : newPos,
    };
    if (editingRule) {
      await updateRule(selected, editingRule.id, data);
    } else {
      await addRule(selected, data);
    }
    setShowRuleForm(false);
    await loadRules(selected);
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm("Delete this rule?")) return;
    await deleteRule(selected, ruleId);
    await loadRules(selected);
  };

  // ── Drag and drop ─────────────────────────────────────────────────────────
  const persistOrder = async (newRules) => {
    setRules(newRules);
    await reorderRules(selected, newRules.map(r => ({
      id: r.id, position: r.position, groupName: r.groupName,
    })));
  };

  // Group drag
  const onGroupDragStart = (e, groupName) => {
    dragGroup.current = groupName;
    e.dataTransfer.effectAllowed = "move";
  };
  const onGroupDragEnd = () => { dragGroup.current = null; };
  const onGroupDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onGroupDrop = (e, targetGroup) => {
    e.preventDefault();
    // A rule is being dragged → move it to this group (cross-group move)
    if (dragRule.current) {
      const src = dragRule.current;
      dragRule.current = null;
      if ((src.groupName || "General") !== targetGroup) {
        const updated = rules.map(r => r.id === src.id ? { ...r, groupName: targetGroup } : r);
        persistOrder(flattenGroups(groupRules(updated)));
      }
      return;
    }
    // Group reorder
    if (!dragGroup.current || dragGroup.current === targetGroup) return;
    const allGroups = groupRules(rules);
    const fromIdx = allGroups.findIndex(g => g.groupName === dragGroup.current);
    const toIdx   = allGroups.findIndex(g => g.groupName === targetGroup);
    if (fromIdx < 0 || toIdx < 0) return;
    const reordered = [...allGroups];
    const [moved]   = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    persistOrder(flattenGroups(reordered));
    dragGroup.current = null;
  };

  // Rule drag within group
  const onRuleDragStart = (e, rule) => {
    dragRule.current = rule;
    e.dataTransfer.effectAllowed = "move";
    e.stopPropagation();
  };
  const onRuleDragEnd = () => { dragRule.current = null; };
  const onRuleDrop = (e, targetRule) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragRule.current || dragRule.current.id === targetRule.id) return;
    if ((dragRule.current.groupName || "General") !== (targetRule.groupName || "General")) return;
    const groups = groupRules(rules);
    const grp    = groups.find(g => g.groupName === (dragRule.current.groupName || "General"));
    if (!grp) return;
    const fromIdx = grp.rules.findIndex(r => r.id === dragRule.current.id);
    const toIdx   = grp.rules.findIndex(r => r.id === targetRule.id);
    if (fromIdx < 0 || toIdx < 0) return;
    const reordered = [...grp.rules];
    const [moved]   = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    grp.rules = reordered;
    persistOrder(flattenGroups(groups));
    dragRule.current = null;
  };

  // ── AI Analysis ───────────────────────────────────────────────────────────
  const handleAiAnalysis = async () => {
    if (!aiSymbol.trim()) return;
    setAiLoading(true); setAiError(""); setAiResult(null);
    const customRules = rules
      .filter(r => r.ruleType === "CUSTOM" && r.description)
      .map(r => r.description);
    const result = await getAiAnalysis(aiSymbol.trim(), customRules);
    if (!result || result.error) {
      setAiError(result?.rationale || "AI analysis failed.");
    } else {
      setAiResult(result);
    }
    setAiLoading(false);
  };

  // ── Validate tab handlers ─────────────────────────────────────────────────
  const loadValSignals = async () => {
    if (!valSymbol.trim()) return;
    setValLoading(true);
    setActiveRule(null); setValSignals(null);
    const data = await getSignalsForSymbol(valSymbol.trim());
    setValSignals(data);
    setValLoading(false);
    loadValEntries();
  };

  const loadValEntries = async (sym) => {
    if (!selected) return;
    setValEntriesLoad(true);
    const entries = await getValidationEntries(selected, sym !== undefined ? sym : (valSymbol.trim() || undefined));
    setValEntries(entries);
    setValEntriesLoad(false);
  };

  // Compute overall result from ruleEvals array
  const computeResult = (ruleEvals) => {
    const techEvals   = ruleEvals.filter(e => !e.isCustom);
    const customEvals = ruleEvals.filter(e => e.isCustom);
    const techTriggered    = techEvals.filter(e => e.triggered).length;
    const customTriggered  = customEvals.filter(e => e.manualOverride === "TRIGGERED").length;
    const customNot        = customEvals.filter(e => e.manualOverride === "NOT_TRIGGERED").length;
    const customPending    = customEvals.filter(e => !e.manualOverride).length;
    const totalTriggered   = techTriggered + customTriggered;
    const totalNot         = (techEvals.length - techTriggered) + customNot;
    if (customPending > 0 && totalTriggered === 0) return "NOT_TRIGGERED";
    if (totalTriggered > 0 && totalNot === 0 && customPending === 0) return "TRIGGERED";
    if (totalTriggered > 0) return "PARTIAL";
    return "NOT_TRIGGERED";
  };

  const handleCustomOverride = (ruleId, toggle) => {
    setActiveRule(prev => {
      if (!prev) return prev;
      const newEvals = prev.ruleEvals.map(e =>
        e.ruleId === ruleId ? { ...e, manualOverride: e.manualOverride === toggle ? null : toggle } : e
      );
      return { ...prev, ruleEvals: newEvals, result: computeResult(newEvals) };
    });
  };

  const applyPill = (ruleRef, isGroup) => {
    if (!valSignals) return;
    // Fix 7: restrict to ruleTypes in THIS strategy only
    const stratRuleTypes = new Set(rules.map(r => r.ruleType));
    const stratSignals   = (valSignals.signals || []).filter(s => stratRuleTypes.has(s.ruleType));

    let ruleEvals;
    if (isGroup) {
      // Fix 6: resolve group→rules from local rules array, NOT from signal.groupName
      const groupRulesList = rules.filter(r => (r.groupName || "General") === ruleRef);
      ruleEvals = groupRulesList.map(r => {
        if (r.ruleType === "CUSTOM") {
          // Fix 5: custom rules need manual override
          return { ruleId: r.id, ruleType: "CUSTOM", label: r.description || "Custom rule", signal: r.signal, isCustom: true, manualOverride: null, triggered: false, reason: null };
        }
        const rt  = RULE_TYPES.find(x => x.value === r.ruleType);
        const sig = stratSignals.find(s => s.ruleType === r.ruleType);
        return { ruleId: r.id, ruleType: r.ruleType, label: rt?.label || r.ruleType, signal: sig?.signal || r.signal, isCustom: false, manualOverride: null, triggered: sig?.triggered ?? false, reason: sig?.reason ?? null };
      });
    } else {
      // Single pill: find the matching local rule
      const localRule = rules.find(r =>
        r.ruleType === "CUSTOM"
          ? `${r.ruleType}:${r.description}` === ruleRef
          : r.ruleType === ruleRef
      );
      if (!localRule) return;
      if (localRule.ruleType === "CUSTOM") {
        // Fix 5: custom rule needs manual check
        ruleEvals = [{ ruleId: localRule.id, ruleType: "CUSTOM", label: localRule.description || "Custom rule", signal: localRule.signal, isCustom: true, manualOverride: null, triggered: false, reason: null }];
      } else {
        const rt  = RULE_TYPES.find(x => x.value === localRule.ruleType);
        const sig = stratSignals.find(s => s.ruleType === localRule.ruleType);
        ruleEvals = [{ ruleId: localRule.id, ruleType: localRule.ruleType, label: rt?.label || localRule.ruleType, signal: sig?.signal || localRule.signal, isCustom: false, manualOverride: null, triggered: sig?.triggered ?? false, reason: sig?.reason ?? null }];
      }
    }

    setActiveRule({ ruleRef, isGroup, result: computeResult(ruleEvals), ruleEvals });
    setNoteText("");
  };

  const handleSaveNote = async () => {
    if (!activeRule || !noteText.trim() || !valSymbol.trim()) return;
    setNoteSaving(true);
    await addValidationEntry(selected, {
      symbol:  valSymbol.trim().toUpperCase(),
      ruleRef: activeRule.ruleRef,
      result:  activeRule.result,
      note:    noteText.trim(),
    });
    setNoteText("");
    await loadValEntries();
    setNoteSaving(false);
  };

  const handleAddFreeNote = async () => {
    if (!noteText.trim() || !valSymbol.trim()) return;
    setNoteSaving(true);
    await addValidationEntry(selected, {
      symbol:  valSymbol.trim().toUpperCase(),
      ruleRef: "Free note",
      result:  "NOTE",
      note:    noteText.trim(),
    });
    setNoteText("");
    await loadValEntries();
    setNoteSaving(false);
  };

  const handleDeleteEntry = async (id) => {
    await deleteValidationEntry(selected, id);
    setValEntries(prev => prev.filter(e => e.id !== id));
  };

  // Derive dominant signal direction from rule evaluations
  const signalFromEvals = (ruleEvals) => {
    if (!ruleEvals?.length) return "BUY";
    const triggered = ruleEvals.filter(e => e.isCustom ? e.manualOverride === "TRIGGERED" : e.triggered);
    const sells = triggered.filter(e => e.signal === "SELL").length;
    const buys  = triggered.filter(e => e.signal === "BUY").length;
    return sells > buys ? "SELL" : "BUY";
  };

  const openPaperTradeModal = async (symbol, tradeType, ruleRef, sourceEntryId = null) => {
    setPtError(""); setPtQty("1"); setPtPrice(""); setPtTradeType(tradeType || "BUY");
    setPtNotes(`Strategy: ${selectedStrategy?.name}${ruleRef ? " | " + ruleRef : ""}`);
    setPtModal({ symbol, ruleRef, sourceEntryId });
    try {
      const list = await getPortfolios();
      const safe = Array.isArray(list) ? list : [];
      setPtPortfolios(safe);
      if (safe.length > 0) setPtPortfolioId(safe[0].id);
    } catch { setPtPortfolios([]); }
  };

  const handlePaperTradeSubmit = async () => {
    if (!ptPortfolioId || !ptPrice || !ptQty) {
      setPtError("Portfolio, price and quantity are all required."); return;
    }
    setPtSubmitting(true); setPtError("");
    try {
      const tradeData = {
        symbol:      ptModal.symbol,
        tradeType:   ptTradeType,
        quantity:    parseInt(ptQty, 10),
        price:       parseFloat(ptPrice),
        strategyTag: selectedStrategy?.name || "",
        notes:       ptNotes || null,
        tradeDate:   new Date().toISOString().split("T")[0],
      };
      const result = await executeTrade(ptPortfolioId, tradeData);
      if (!result?.id) {
        setPtError("Failed to create trade — check portfolio cash balance."); setPtSubmitting(false); return;
      }
      // Link trade to an existing journal entry if coming from one
      if (ptModal.sourceEntryId) {
        const linked = await linkPaperTrade(selected, ptModal.sourceEntryId, result.id);
        if (linked) setValEntries(prev => prev.map(e => e.id === ptModal.sourceEntryId ? linked : e));
      } else {
        // Coming from eval result: auto-create a journal entry and link it
        const autoNote = await addValidationEntry(selected, {
          symbol:  ptModal.symbol,
          ruleRef: ptModal.ruleRef || "Paper trade",
          result:  activeRule?.result || "NOTE",
          note:    `📈 Paper trade: ${ptTradeType} ${ptQty}×${ptModal.symbol} @ ₹${ptPrice}`,
          outcome: "PENDING",
        });
        if (autoNote?.id) {
          const linked = await linkPaperTrade(selected, autoNote.id, result.id);
          setValEntries(prev => [linked || autoNote, ...prev]);
        }
      }
      setPtModal(null);
    } catch (err) {
      setPtError("Error: " + (err.message || "unknown"));
    }
    setPtSubmitting(false);
  };

  const handleUpdateOutcome = async (id, outcome) => {
    setUpdatingOutcome(id);
    const updated = await updateValidationOutcome(selected, id, outcome);
    if (updated) setValEntries(prev => prev.map(e => e.id === id ? updated : e));
    setUpdatingOutcome(null);
  };

  const onPillDragStart = (e, ruleRef, isGroup) => {
    dragPill.current = { ruleRef, isGroup };
    e.dataTransfer.effectAllowed = "copy";
  };
  const onPillDragEnd = () => { dragPill.current = null; };
  const onDropZoneDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; };
  const onDropZoneDrop = (e) => {
    e.preventDefault();
    if (!dragPill.current) return;
    applyPill(dragPill.current.ruleRef, dragPill.current.isGroup);
    dragPill.current = null;
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const selectedStrategy = strategies.find(s => s.id === selected);
  const groups        = useMemo(() => groupRules(rules), [rules]);
  const existingGroups = useMemo(() => [...new Set(rules.map(r => r.groupName || "General"))], [rules]);

  return (
    <div className="strat-page">

      {/* ── Left: Strategy list ───────────────────────────────────────────── */}
      <div className="strat-sidebar">
        <div className="strat-sidebar-header">
          <span className="strat-sidebar-title">Strategies</span>
          <button className="btn btn-sm" onClick={() => setShowNewStrat(true)}>+</button>
        </div>

        {loading && <div className="strat-muted">Loading…</div>}

        {!loading && strategies.length === 0 && (
          <div style={{ padding: "12px 0" }}>
            <p className="strat-muted" style={{ marginBottom: 10 }}>No strategies yet.</p>
            <button className="btn btn-sm" style={{ width: "100%" }} onClick={handleSeed}>
              🚀 Load starters
            </button>
          </div>
        )}

        {strategies.map(s => (
          <button key={s.id}
            className={`strat-list-item ${selected === s.id ? "active" : ""}`}
            onClick={() => setSelected(s.id)}>
            <span className="strat-list-name">{s.name}</span>
            {!s.active && <span className="strat-inactive-badge">off</span>}
          </button>
        ))}
      </div>

      {/* ── Right: Detail panel ───────────────────────────────────────────── */}
      <div className="strat-detail">
        {!selectedStrategy ? (
          <div className="strat-empty">Select or create a strategy to get started.</div>
        ) : (
          <>
            {/* Strategy header */}
            <div className="strat-detail-header">
              <div>
                <div className="strat-detail-name">{selectedStrategy.name}</div>
                {selectedStrategy.description && (
                  <div className="strat-detail-desc">{selectedStrategy.description}</div>
                )}
                {selectedStrategy.tags && (
                  <div className="strat-tag-row">
                    {selectedStrategy.tags.split(",").map(t => (
                      <span key={t} className="strat-tag">{t.trim()}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="strat-detail-actions">
                <button className="btn btn-ghost btn-sm"
                  onClick={() => handleToggleActive(selectedStrategy)}>
                  {selectedStrategy.active ? "Deactivate" : "Activate"}
                </button>
                <button className="btn btn-ghost btn-sm"
                  style={{ color: "var(--color-danger)" }}
                  onClick={() => handleDeleteStrategy(selected)}>Delete</button>
              </div>
            </div>

            {/* Tabs */}
            <div className="strat-tabs">
              {[
                { id: "playbook",  label: "📋 Playbook" },
                { id: "ai",        label: "🤖 AI Analysis" },
                { id: "screener",  label: "📡 Screener" },
                { id: "validate",  label: "🔬 Validate" },
              ].map(t => (
                <button key={t.id}
                  className={`strat-tab ${detailTab === t.id ? "active" : ""}`}
                  onClick={() => setDetailTab(t.id)}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* ── Playbook tab ─────────────────────────────────────────────── */}
            {detailTab === "playbook" && (
              <div className="strat-playbook">
                <div className="strat-playbook-toolbar">
                  <span className="strat-muted" style={{ fontSize: 12 }}>
                    {rules.length} rule{rules.length !== 1 ? "s" : ""} in {groups.length} group{groups.length !== 1 ? "s" : ""}
                    {" · drag groups or rules to reorder"}
                  </span>
                  <button className="btn btn-sm" onClick={() => openRuleForm(null)}>+ Add rule</button>
                </div>

                {rulesLoading && <div className="strat-muted">Loading rules…</div>}

                {!rulesLoading && rules.length === 0 && (
                  <div className="strat-empty-rules">
                    <p>No rules yet. Add your first rule to define when to BUY or SELL.</p>
                    <button className="btn" onClick={() => openRuleForm(null)}>+ Add first rule</button>
                  </div>
                )}

                {!rulesLoading && groups.map(group => (
                  <div key={group.groupName}
                    className="strat-group"
                    draggable
                    onDragStart={e => onGroupDragStart(e, group.groupName)}
                    onDragEnd={onGroupDragEnd}
                    onDragOver={onGroupDragOver}
                    onDrop={e => onGroupDrop(e, group.groupName)}>

                    <div className="strat-group-header">
                      <span className="strat-drag-handle" title="Drag to reorder group">⠿</span>
                      <span className="strat-group-name">{group.groupName}</span>
                      <span className="strat-group-count">{group.rules.length}</span>
                      <button className="btn btn-ghost btn-sm strat-group-add"
                        onClick={() => openRuleForm(null, group.groupName)}>+ Rule</button>
                    </div>

                    <div className="strat-rule-list">
                      {group.rules.map(r => {
                        const rt = RULE_TYPES.find(x => x.value === r.ruleType);
                        return (
                          <div key={r.id}
                            className="strat-rule-item"
                            draggable
                            onDragStart={e => onRuleDragStart(e, r)}
                            onDragEnd={onRuleDragEnd}
                            onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                            onDrop={e => onRuleDrop(e, r)}>
                            <span className="strat-drag-handle strat-rule-drag">⠿</span>
                            <span className="strat-signal-dot"
                              style={{ background: SIGNAL_COLOR[r.signal] }}
                              title={r.signal} />
                            <div className="strat-rule-body">
                              <div className="strat-rule-label">
                                {rt?.label || r.ruleType}
                                {r.ruleType === "CUSTOM" && <span className="strat-learned-badge">⭐ learned</span>}
                              </div>
                              {r.description && (
                                <div className="strat-rule-desc">{r.description}</div>
                              )}
                              {renderRuleParams(r.parameters)}
                              {r.learnedOn && (
                                <div className="strat-rule-meta">learned {r.learnedOn}</div>
                              )}
                            </div>
                            <span className={`strat-signal-badge ${r.signal === "BUY" ? "buy" : "sell"}`}>
                              {r.signal}
                            </span>
                            <div className="strat-rule-actions">
                              <button onClick={() => openRuleForm(r)}>✏</button>
                              <button onClick={() => handleDeleteRule(r.id)}>✕</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── AI Analysis tab ──────────────────────────────────────────── */}
            {detailTab === "ai" && (
              <div className="strat-ai">
                <div className="strat-ai-input-row">
                  <input
                    className="strat-ai-symbol-input"
                    placeholder="Enter stock symbol, e.g. RELIANCE"
                    value={aiSymbol}
                    onChange={e => setAiSymbol(e.target.value.toUpperCase())}
                    onKeyDown={e => e.key === "Enter" && handleAiAnalysis()}
                  />
                  <button className="btn" onClick={handleAiAnalysis} disabled={aiLoading || !aiSymbol.trim()}>
                    {aiLoading ? "Analyzing…" : "🤖 Get AI Analysis"}
                  </button>
                </div>

                {rules.filter(r => r.ruleType === "CUSTOM").length > 0 && (
                  <p className="strat-muted" style={{ fontSize: 12, marginBottom: 12 }}>
                    {rules.filter(r => r.ruleType === "CUSTOM").length} custom rule(s) from this strategy will be included as context for the AI.
                  </p>
                )}

                {aiError && (
                  <div className="strat-ai-error">{aiError}</div>
                )}

                {aiResult && (
                  <div className="strat-ai-result">
                    <div className="strat-ai-hero">
                      <span className="strat-ai-symbol">{aiResult.symbol}</span>
                      <span className="strat-ai-rec" style={{ background: AI_COLOR[aiResult.recommendation] }}>
                        {aiResult.recommendation}
                      </span>
                      <span className="strat-ai-conf" style={{ color: CONF_COLOR[aiResult.confidenceLevel] }}>
                        {aiResult.confidenceLevel} confidence
                      </span>
                      <span className="strat-ai-timeframe">{aiResult.timeframe}</span>
                    </div>

                    <div className="strat-ai-rationale">{aiResult.rationale}</div>

                    <div className="strat-ai-cols">
                      <div className="strat-ai-col">
                        <div className="strat-ai-col-title">📈 Technical</div>
                        <p className="strat-ai-col-body">{aiResult.technicalOutlook}</p>
                      </div>
                      <div className="strat-ai-col">
                        <div className="strat-ai-col-title">🏢 Fundamental</div>
                        <p className="strat-ai-col-body">{aiResult.fundamentalOutlook}</p>
                      </div>
                    </div>

                    {Array.isArray(aiResult.keyFactors) && aiResult.keyFactors.length > 0 && (
                      <div className="strat-ai-section">
                        <div className="strat-ai-section-title">Key Factors</div>
                        <ul className="strat-ai-list">
                          {aiResult.keyFactors.map((f, i) => <li key={i}>{f}</li>)}
                        </ul>
                      </div>
                    )}

                    {Array.isArray(aiResult.risks) && aiResult.risks.length > 0 && (
                      <div className="strat-ai-section">
                        <div className="strat-ai-section-title strat-ai-risks-title">Risks</div>
                        <ul className="strat-ai-list strat-ai-risks">
                          {aiResult.risks.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                      </div>
                    )}

                    <p className="strat-ai-disclaimer">
                      ⚠ AI-generated analysis. Not financial advice. Always do your own research.
                    </p>
                  </div>
                )}

                {!aiResult && !aiError && !aiLoading && (
                  <div className="strat-empty" style={{ marginTop: 32 }}>
                    Enter a stock symbol above to get an AI-powered buy/sell/hold recommendation.
                    {rules.length > 0 && " Your custom rules will be used as additional context."}
                  </div>
                )}
              </div>
            )}

            {/* ── Screener tab ─────────────────────────────────────────────── */}
            {detailTab === "screener" && (
              <div className="strat-screener">
                <div className="strat-screener-bar">
                  <span className="strat-muted" style={{ fontSize: 13 }}>
                    Evaluates all active strategies against your watchlist stocks.
                  </span>
                  <button className="btn btn-sm" onClick={loadScreener} disabled={screenerLoading}>
                    {screenerLoading ? "Running…" : "↻ Refresh"}
                  </button>
                </div>

                {screenerLoading && <div className="strat-muted">Evaluating…</div>}

                {!screenerLoading && screenerData.length === 0 && (
                  <div className="strat-empty">
                    No signals. Make sure you have active strategies with rules AND stocks in your Watchlist with price history.
                  </div>
                )}

                {screenerData.map(stock => (
                  <div key={stock.symbol} className="strat-screener-card">
                    <div className="strat-screener-card-header">
                      <span className="strat-screener-symbol">{stock.symbol}</span>
                      {stock.stockName && <span className="strat-muted">{stock.stockName}</span>}
                      {stock.latestPrice && <span className="strat-screener-price">₹{Number(stock.latestPrice).toFixed(2)}</span>}
                      {stock.buyCount  > 0 && <span className="strat-signal-pill buy">🟢 {stock.buyCount} BUY</span>}
                      {stock.sellCount > 0 && <span className="strat-signal-pill sell">🔴 {stock.sellCount} SELL</span>}
                      {onOpenChart && (
                        <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }}
                          onClick={() => onOpenChart(stock.symbol, stock.stockName)}>Chart</button>
                      )}
                    </div>
                    {(stock.signals || []).filter(s => s.triggered).map((sig, i) => (
                      <div key={i} className={`strat-signal-row ${sig.signal === "BUY" ? "buy" : "sell"}`}>
                        <span className="strat-signal-label">{sig.signal}</span>
                        <span>{sig.strategyName} — {RULE_TYPES.find(r => r.value === sig.ruleType)?.label || sig.ruleType}</span>
                        <span className="strat-muted" style={{ fontSize: 11 }}>{sig.reason}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* ── Validate tab ──────────────────────────────────────────────── */}
            {detailTab === "validate" && (
              <div className="strat-validate">

                {/* Symbol input row */}
                <div className="strat-val-header">
                  <input
                    className="strat-ai-symbol-input"
                    placeholder="Stock symbol, e.g. RELIANCE"
                    value={valSymbol}
                    onChange={e => setValSymbol(e.target.value.toUpperCase())}
                    onKeyDown={e => e.key === "Enter" && loadValSignals()}
                  />
                  <button className="btn" onClick={loadValSignals} disabled={valLoading || !valSymbol.trim()}>
                    {valLoading ? "Loading…" : "Load Signals"}
                  </button>
                </div>

                {!valSignals && !valLoading && (
                  <div className="strat-empty" style={{ marginTop: 16 }}>
                    Enter a stock symbol above to load its signals, then drag a rule pill to evaluate it.
                  </div>
                )}

                {valSignals && (
                  <>
                    {rules.some(r => r.ruleType === "CUSTOM") && (
                      <div style={{ background: "rgba(245,158,11,.08)", border: "1px solid rgba(245,158,11,.25)", borderRadius: 6, padding: "7px 12px", marginBottom: 10, fontSize: 12, color: "var(--color-text-secondary)" }}>
                        ⭐ This strategy has <strong>{rules.filter(r => r.ruleType === "CUSTOM").length} custom rule(s)</strong> — click their pills below to evaluate manually using the ✅ Yes / ❌ No buttons.
                      </div>
                    )}
                    {rules.length === 0 && (
                      <div style={{ background: "rgba(99,102,241,.08)", border: "1px solid rgba(99,102,241,.25)", borderRadius: 6, padding: "7px 12px", marginBottom: 10, fontSize: 12, color: "var(--color-text-secondary)" }}>
                        💡 No rules in this strategy yet. Go to the <strong>Playbook</strong> tab → <strong>+ Add rule</strong> → select <em>"Custom / learned rule"</em> to add your own observations, or pick a technical rule type.
                      </div>
                    )}
                    {/* Rule pill library */}
                    <div className="strat-pill-section">
                      <div className="strat-pill-section-label">Individual rules — drag or click to evaluate</div>
                      {groups.map(group => (
                        <div key={group.groupName} className="strat-pill-group">
                          <span className="strat-pill-group-name">{group.groupName}</span>
                          <div className="strat-pill-row">
                            {group.rules.map(r => {
                              const rt = RULE_TYPES.find(x => x.value === r.ruleType);
                              const pillRef = r.ruleType === "CUSTOM"
                                ? `${r.ruleType}:${r.description}`
                                : r.ruleType;
                              return (
                                <div key={r.id}
                                  className={`strat-pill ${r.signal === "BUY" ? "buy" : "sell"} ${r.ruleType === "CUSTOM" ? "custom" : ""} ${activeRule?.ruleRef === pillRef ? "active" : ""}`}
                                  draggable
                                  onDragStart={e => onPillDragStart(e, pillRef, false)}
                                  onDragEnd={onPillDragEnd}
                                  onClick={() => applyPill(pillRef, false)}
                                  title={r.ruleType === "CUSTOM" ? "Custom rule — click to manually evaluate" : "Drag or click to evaluate"}>
                                  <span className="strat-pill-dot" style={{ background: SIGNAL_COLOR[r.signal] }} />
                                  {r.ruleType === "CUSTOM" && <span style={{ fontSize: 10 }}>⭐</span>}
                                  <span className="strat-pill-label">{r.ruleType === "CUSTOM" ? (r.description?.slice(0, 22) || "Custom rule") : (rt?.label || r.ruleType)}</span>
                                  {r.description && <span className="strat-pill-desc">{r.description.length > 20 ? r.description.slice(0, 18) + "…" : r.description}</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Group packages */}
                    {groups.length > 0 && (
                      <div className="strat-pill-section">
                        <div className="strat-pill-section-label">Group packages — apply all rules in a group at once</div>
                        <div className="strat-pill-row" style={{ flexWrap: "wrap" }}>
                          {groups.map(group => (
                            <div key={group.groupName}
                              className={`strat-package ${activeRule?.ruleRef === group.groupName && activeRule.isGroup ? "active" : ""}`}
                              draggable
                              onDragStart={e => onPillDragStart(e, group.groupName, true)}
                              onDragEnd={onPillDragEnd}
                              onClick={() => applyPill(group.groupName, true)}
                              title="Drag or click to apply all rules in this group">
                              <span className="strat-package-icon">📦</span>
                              <span className="strat-package-name">{group.groupName}</span>
                              <span className="strat-package-count">{group.rules.length} rules</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Drop zone / Evaluation result */}
                    <div className="strat-drop-zone"
                      onDragOver={onDropZoneDragOver}
                      onDrop={onDropZoneDrop}>
                      {!activeRule && (
                        <div className="strat-drop-hint">
                          ⬆ Drag a rule pill or package here, or click one above to evaluate against <strong>{valSymbol}</strong>
                        </div>
                      )}

                      {activeRule && (
                        <div className="strat-eval-result">
                          <div className="strat-eval-header">
                            <span className="strat-eval-label">
                              {activeRule.isGroup ? "📦 Package" : "Rule"}: <strong>{activeRule.ruleRef}</strong>
                            </span>
                            <span className={`strat-eval-badge ${activeRule.result.toLowerCase()}`}>
                              {activeRule.result === "TRIGGERED"     && "✅ Triggered"}
                              {activeRule.result === "NOT_TRIGGERED" && "❌ Not triggered"}
                              {activeRule.result === "PARTIAL"       && "⚠️ Partial"}
                              {activeRule.ruleEvals?.some(e => e.isCustom && !e.manualOverride) && " (pending manual checks)"}
                            </span>
                          </div>

                          {activeRule.ruleEvals?.length > 0 ? (
                            <div className="strat-eval-signals">
                              {activeRule.ruleEvals.map((e, i) => (
                                <div key={i} className={`strat-eval-signal-row ${e.isCustom ? "custom" : e.triggered ? "triggered" : "not"}`}>
                                  {e.isCustom ? (
                                    <>
                                      <span>🔍</span>
                                      <span style={{ fontSize: 12, flex: 1 }}>{e.label}</span>
                                      <span className="strat-muted" style={{ fontSize: 11, marginRight: 6 }}>Manual check</span>
                                      <div className="strat-custom-override">
                                        <button
                                          className={`btn btn-sm ${e.manualOverride === "TRIGGERED" ? "strat-override-yes-active" : "btn-ghost"}`}
                                          onClick={() => handleCustomOverride(e.ruleId, "TRIGGERED")}>✅ Yes</button>
                                        <button
                                          className={`btn btn-sm ${e.manualOverride === "NOT_TRIGGERED" ? "strat-override-no-active" : "btn-ghost"}`}
                                          onClick={() => handleCustomOverride(e.ruleId, "NOT_TRIGGERED")}>❌ No</button>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <span>{e.triggered ? "✅" : "❌"}</span>
                                      <span className="strat-signal-badge" style={{ background: e.signal === "BUY" ? "rgba(34,197,94,.15)" : "rgba(239,68,68,.15)", color: e.signal === "BUY" ? "#22c55e" : "#ef4444", borderRadius: "999px", padding: "1px 6px", fontSize: "10px", fontWeight: 700 }}>{e.signal || "—"}</span>
                                      <span style={{ fontSize: 12 }}>{e.label}</span>
                                      {e.reason && <span className="strat-eval-reason">{e.reason}</span>}
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="strat-muted" style={{ marginTop: 8 }}>No data for this rule against {valSymbol}. Stock may not have enough price history.</p>
                          )}

                          {/* Note input */}
                          <div className="strat-eval-note-area">
                            <div className="strat-eval-note-label">Add your observation / why this matters:</div>
                            <textarea
                              className="form-input"
                              rows={2}
                              placeholder="e.g. RSI was 17 when I noticed this. Price bounced strongly off 52w low…"
                              value={noteText}
                              onChange={e => setNoteText(e.target.value)}
                            />
                            <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                              <button className="btn btn-sm" onClick={handleSaveNote} disabled={!noteText.trim() || noteSaving}>
                                {noteSaving ? "Saving…" : "💾 Save note"}
                              </button>
                              {(activeRule.result === "TRIGGERED" || activeRule.result === "PARTIAL") && (
                                <button className="btn btn-sm strat-paper-trade-btn"
                                  onClick={() => openPaperTradeModal(
                                    valSymbol,
                                    signalFromEvals(activeRule.ruleEvals),
                                    activeRule.ruleRef,
                                    null
                                  )}>
                                  📈 Paper Trade this
                                </button>
                              )}
                              <button className="btn btn-ghost btn-sm" onClick={() => setActiveRule(null)}>Clear</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Free note (without evaluating a specific rule) */}
                    {!activeRule && (
                      <div className="strat-free-note">
                        <div className="strat-pill-section-label">General observation for {valSymbol}</div>
                        <textarea
                          className="form-input"
                          rows={2}
                          placeholder="Add a free-form note about this stock without linking to a specific rule…"
                          value={noteText}
                          onChange={e => setNoteText(e.target.value)}
                        />
                        <button className="btn btn-sm" style={{ marginTop: 6 }}
                          onClick={handleAddFreeNote} disabled={!noteText.trim() || noteSaving}>
                          {noteSaving ? "Saving…" : "💾 Save note"}
                        </button>
                      </div>
                    )}
                  </>
                )}

                {/* Decision journal */}
                <div className="strat-journal">
                  <div className="strat-journal-header">
                    <span className="strat-pill-section-label" style={{ margin: 0 }}>
                      Decision journal {valSymbol ? `— ${valSymbol}` : "(all symbols)"}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {valEntries.length > 0 && (() => {
                        const decided = valEntries.filter(e => e.outcome === "WIN" || e.outcome === "LOSS");
                        const wins    = valEntries.filter(e => e.outcome === "WIN").length;
                        return decided.length > 0 ? (
                          <span className="strat-accuracy-badge">
                            🎯 {Math.round((wins / decided.length) * 100)}% accuracy ({wins}/{decided.length})
                          </span>
                        ) : null;
                      })()}
                      <button className="btn btn-ghost btn-sm"
                        onClick={() => loadValEntries(undefined)}
                        disabled={valEntriesLoad}>
                        {valEntriesLoad ? "…" : "↻"}
                      </button>
                    </div>
                  </div>

                  {valEntries.length === 0 && !valEntriesLoad && (
                    <div className="strat-muted" style={{ padding: "12px 0" }}>
                      No entries yet. Evaluate rules above and add notes to build your decision journal.
                    </div>
                  )}

                  {valEntries.map(entry => (
                    <div key={entry.id} className="strat-journal-entry">
                      <div className="strat-journal-entry-header">
                        <span className="strat-journal-symbol">{entry.symbol}</span>
                        {entry.result !== "NOTE" && (
                          <span className={`strat-eval-badge ${entry.result?.toLowerCase()}`}>
                            {entry.result === "TRIGGERED"     && "✅ Triggered"}
                            {entry.result === "NOT_TRIGGERED" && "❌ Not triggered"}
                            {entry.result === "PARTIAL"       && "⚠️ Partial"}
                          </span>
                        )}
                        {entry.ruleRef && entry.ruleRef !== "Free note" && (
                          <span className="strat-journal-rule-ref">{entry.ruleRef}</span>
                        )}
                        <span className="strat-journal-time">
                          {new Date(entry.createdAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        </span>
                        {!entry.linkedPaperTradeId && (
                          <button className="strat-paper-trade-btn-sm"
                            title="Create paper trade from this decision"
                            onClick={() => openPaperTradeModal(
                              entry.symbol,
                              entry.result === "NOT_TRIGGERED" ? "SELL" : "BUY",
                              entry.ruleRef,
                              entry.id
                            )}>
                            📈
                          </button>
                        )}
                        {entry.linkedPaperTradeId && (
                          <span className="strat-linked-trade-badge" title={`Linked trade: ${entry.linkedPaperTradeId}`}>
                            📈 {entry.linkedPaperTradeId.slice(0, 12)}…
                          </span>
                        )}
                        <button className="strat-journal-del" onClick={() => handleDeleteEntry(entry.id)} title="Delete">✕</button>
                      </div>
                      <div className="strat-journal-note-text">{entry.note}</div>
                      {/* Outcome tracking */}
                      <div className="strat-outcome-row">
                        <span className="strat-muted" style={{ fontSize: 11 }}>Outcome:</span>
                        {["PENDING", "WIN", "LOSS", "NEUTRAL"].map(o => (
                          <button key={o}
                            className={`strat-outcome-btn ${entry.outcome === o ? `active-${o.toLowerCase()}` : ""}`}
                            disabled={updatingOutcome === entry.id}
                            onClick={() => handleUpdateOutcome(entry.id, o)}>
                            {o === "PENDING" ? "⏳" : o === "WIN" ? "✅" : o === "LOSS" ? "❌" : "➖"} {o}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            )}
          </>
        )}
      </div>

      {/* ── New Strategy modal ────────────────────────────────────────────── */}
      {showNewStrat && (
        <div className="modal-overlay" onClick={() => setShowNewStrat(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">New Strategy</div>
            <label className="form-label">Name *</label>
            <input className="form-input" value={stratName}
              onChange={e => setStratName(e.target.value)} placeholder="e.g. Reliance Swing Play" />
            <label className="form-label">Description</label>
            <textarea className="form-input" rows={2} value={stratDesc}
              onChange={e => setStratDesc(e.target.value)} placeholder="What is this strategy about?" />
            <label className="form-label">Tags (comma-separated)</label>
            <input className="form-input" value={stratTags}
              onChange={e => setStratTags(e.target.value)} placeholder="swing,beginner" />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button className="btn" onClick={handleCreateStrategy} disabled={!stratName.trim()}>Create</button>
              <button className="btn btn-ghost" onClick={() => setShowNewStrat(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Paper trade modal ────────────────────────────────────────────── */}
      {ptModal && (
        <div className="modal-overlay" onClick={() => setPtModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-title">📈 Paper Trade — {ptModal.symbol}</div>

            {ptPortfolios.length === 0 && (
              <div className="strat-ai-error" style={{ marginBottom: 10 }}>
                No portfolios found. Create a portfolio in the Paper Trading tab first.
              </div>
            )}

            <label className="form-label">Portfolio</label>
            <select className="form-input" value={ptPortfolioId} onChange={e => setPtPortfolioId(e.target.value)}>
              {ptPortfolios.map(p => <option key={p.id} value={p.id}>{p.name} (₹{Number(p.currentCash || 0).toLocaleString("en-IN")} cash)</option>)}
            </select>

            <label className="form-label">Direction</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              {["BUY", "SELL"].map(t => (
                <button key={t} className={`btn btn-sm ${ptTradeType === t ? "" : "btn-ghost"}`}
                  style={ptTradeType === t ? { background: t === "BUY" ? "#22c55e" : "#ef4444" } : {}}
                  onClick={() => setPtTradeType(t)}>{t}</button>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label className="form-label">Quantity</label>
                <input className="form-input" type="number" min="1" value={ptQty}
                  onChange={e => setPtQty(e.target.value)} placeholder="10" />
              </div>
              <div>
                <label className="form-label">Entry price ₹</label>
                <input className="form-input" type="number" step="0.01" value={ptPrice}
                  onChange={e => setPtPrice(e.target.value)} placeholder="2450.00" autoFocus />
              </div>
            </div>

            {ptPrice && ptQty && (
              <div className="strat-pt-summary">
                Total: ₹{(parseFloat(ptPrice) * parseInt(ptQty, 10)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </div>
            )}

            <label className="form-label">Notes (auto-filled from strategy context)</label>
            <textarea className="form-input" rows={2} value={ptNotes} onChange={e => setPtNotes(e.target.value)} />

            {ptError && <div className="strat-ai-error" style={{ marginTop: 8 }}>{ptError}</div>}

            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button className="btn" onClick={handlePaperTradeSubmit}
                disabled={ptSubmitting || !ptPortfolioId || !ptPrice || !ptQty}>
                {ptSubmitting ? "Creating…" : `${ptTradeType} ${ptQty || "—"} × ${ptModal.symbol}`}
              </button>
              <button className="btn btn-ghost" onClick={() => setPtModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Rule form modal ───────────────────────────────────────────────── */}
      {showRuleForm && (
        <div className="modal-overlay" onClick={() => setShowRuleForm(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-title">{editingRule ? "Edit Rule" : "Add Rule"}</div>

            <label className="form-label">Group</label>
            <input className="form-input" list="group-suggestions" value={ruleGroupName}
              onChange={e => setRuleGroupName(e.target.value)}
              placeholder="e.g. Technical, Price Level, Patterns" />
            <datalist id="group-suggestions">
              {existingGroups.map(g => <option key={g} value={g} />)}
            </datalist>

            <label className="form-label">Signal</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              {["BUY", "SELL"].map(s => (
                <button key={s} className={`btn btn-sm ${ruleSignal === s ? "" : "btn-ghost"}`}
                  style={ruleSignal === s ? { background: SIGNAL_COLOR[s] } : {}}
                  onClick={() => setRuleSignal(s)}>{s}</button>
              ))}
            </div>

            <label className="form-label">Rule type</label>
            <select className="form-input" value={ruleType} onChange={e => setRuleType(e.target.value)}>
              {RULE_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>

            {ruleType === "CUSTOM" && (
              <div style={{ background: "rgba(245,158,11,.1)", border: "1px solid rgba(245,158,11,.3)", borderRadius: 6, padding: "8px 12px", marginTop: 6, fontSize: 12, color: "var(--color-text-secondary)" }}>
                ⭐ <strong>Custom / Learned rule</strong> — Write what you've personally learned or observed about this stock's behaviour. It cannot be auto-evaluated; you'll manually mark it ✅ Yes / ❌ No in the <strong>Validate</strong> tab. The description is required and will appear as a pill.
              </div>
            )}

            {RULE_TYPES.find(r => r.value === ruleType)?.hasThreshold && (
              <>
                <label className="form-label">Threshold</label>
                <input className="form-input" type="number" value={ruleThresh}
                  onChange={e => setRuleThresh(e.target.value)} />
              </>
            )}
            {RULE_TYPES.find(r => r.value === ruleType)?.hasPeriod && (
              <>
                <label className="form-label">SMA period</label>
                <input className="form-input" type="number" value={rulePeriod}
                  onChange={e => setRulePeriod(e.target.value)} />
              </>
            )}
            {RULE_TYPES.find(r => r.value === ruleType)?.hasPct && (
              <>
                <label className="form-label">Percentage (%)</label>
                <input className="form-input" type="number" value={rulePct}
                  onChange={e => setRulePct(e.target.value)} />
              </>
            )}

            <label className="form-label">
              {ruleType === "CUSTOM" ? "Your observation / pattern (required)" : "Description / what I learned"}
              {ruleType === "CUSTOM" && <span style={{ color: "var(--color-danger)" }}> *</span>}
            </label>
            <textarea className="form-input" rows={3} value={ruleDesc}
              onChange={e => setRuleDesc(e.target.value)}
              style={ruleType === "CUSTOM" && !ruleDesc.trim() ? { borderColor: "var(--color-warning, #f59e0b)" } : {}}
              placeholder={ruleType === "CUSTOM"
                ? "e.g. Stock always consolidates for 3–5 days before a breakout. Look for narrowing candles with low volume."
                : "Describe the pattern, what you observed, when this fires…"} />

            <label className="form-label">Date learned (optional)</label>
            <input className="form-input" type="date" value={ruleLearnedOn}
              onChange={e => setRuleLearnedOn(e.target.value)} />

            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button className="btn" onClick={handleSaveRule}>Save</button>
              <button className="btn btn-ghost" onClick={() => setShowRuleForm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
